import { useEffect, useRef } from 'react'

import { Check } from 'lucide-react'
import { useInView } from 'motion/react'

import { track } from '@/api/tracking'
import { FOOD_COST_LANDING_PAGE } from '@/components/food-cost/constants'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { ROUTES } from '@/navigation/constants'

import {
    ASESORIA_ANCHOR,
    OFFER_PATHS,
    type OfferPathId,
    RECOMMENDED_PLAN_BADGE,
    type RecommendedPlanId,
    TOOL_ONLY_EXIT,
    VIAS_LANDING_VARIANT,
} from './offerPaths'

interface ViasOfferSectionProps {
    /** Plan recommended by the /empieza quiz; featured instead of the default one. */
    recommendedPlan?: RecommendedPlanId | null
    onSelectPath: (pathId: OfferPathId) => void
}

export function ViasOfferSection({ recommendedPlan, onSelectPath }: ViasOfferSectionProps) {
    const sectionRef = useRef<HTMLElement>(null)
    const isInView = useInView(sectionRef, { once: true })

    useEffect(() => {
        if (!isInView) return
        track('Pricing', {
            landing_page: FOOD_COST_LANDING_PAGE,
            landing_variant: VIAS_LANDING_VARIANT,
            recommended_plan: recommendedPlan ?? undefined,
        })
    }, [isInView, recommendedPlan])

    const trackToolOnlyExit = () => {
        track('Offer Path Selected', {
            landing_page: FOOD_COST_LANDING_PAGE,
            landing_variant: VIAS_LANDING_VARIANT,
            offer_path: 'herramienta_exit',
        })
    }

    return (
        <section id='vias' ref={sectionRef} className='scroll-mt-24 bg-white py-[60px] md:py-20'>
            <div className='mx-auto max-w-[1180px] px-[22px] md:px-10'>
                <h2 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#F4502A] md:text-[46px]'>
                    Una opción para cada situación
                </h2>

                <div className='mt-10 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
                    {OFFER_PATHS.map(path => {
                        const isFeatured = path.id === (recommendedPlan ?? 'local_rentable')
                        // With a quiz recommendation, only that plan carries a badge.
                        const badge = recommendedPlan
                            ? recommendedPlan === path.id
                                ? RECOMMENDED_PLAN_BADGE
                                : undefined
                            : path.badge
                        return (
                            <article
                                key={path.id}
                                className={cn(
                                    'flex flex-col rounded-[28px] border p-6 md:p-7',
                                    isFeatured
                                        ? 'border-[#17130E] bg-[#17130E] text-[#F5EEE4]'
                                        : 'border-[#E4DCD0] bg-[#F5EEE4] text-[#17130E]',
                                )}
                            >
                                <div className='flex items-start justify-between gap-2'>
                                    <h3 className='m-0 text-[20px] font-semibold leading-tight tracking-[-0.02em] md:text-[22px]'>
                                        {path.label}
                                    </h3>
                                    {badge ? (
                                        <span className='shrink-0 rounded-full bg-[#F4502A] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white'>
                                            {badge}
                                        </span>
                                    ) : null}
                                </div>

                                <p
                                    className={cn(
                                        'mt-3 text-[14px] leading-[1.5] md:text-[15px]',
                                        isFeatured ? 'text-[#C9C0B3]' : 'text-[#5C5548]',
                                    )}
                                >
                                    {path.forYouIf}
                                </p>

                                <div className='mt-5'>
                                    <p className='m-0 text-[36px] font-extrabold leading-none tracking-[-0.035em] md:text-[40px]'>
                                        {path.price}
                                    </p>
                                    <p
                                        className={cn(
                                            'mt-2 text-[13px] leading-snug',
                                            isFeatured ? 'text-[#A89F90]' : 'text-[#8A7F6F]',
                                        )}
                                    >
                                        {path.priceNote}
                                    </p>
                                </div>

                                <ul className='m-0 mt-5 flex flex-1 list-none flex-col gap-3 p-0'>
                                    {path.includes.map(item => (
                                        <li key={item} className='flex items-start gap-2.5'>
                                            <Check
                                                className='mt-0.5 h-[18px] w-[18px] shrink-0 text-[#F4502A]'
                                                strokeWidth={2.75}
                                                aria-hidden
                                            />
                                            <span
                                                className={cn(
                                                    'text-[14px] font-medium leading-snug md:text-[15px]',
                                                    isFeatured ? 'text-[#F5EEE4]' : 'text-[#17130E]',
                                                )}
                                            >
                                                {item}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    type='button'
                                    onClick={() => onSelectPath(path.id)}
                                    className={cn(
                                        'mt-6 inline-flex min-h-12 w-full items-center justify-center rounded-full px-4 text-[14px] font-bold transition-colors',
                                        isFeatured
                                            ? 'bg-[#D13D1A] text-white hover:bg-[#C03214]'
                                            : 'bg-[#17130E] text-[#F5EEE4] hover:bg-[#241A12]',
                                    )}
                                >
                                    {path.cta}
                                </button>
                            </article>
                        )
                    })}
                </div>

                <article className='relative mt-10 overflow-hidden rounded-[28px] border border-[#E4D5A8]/55 bg-[#241C16] px-6 py-8 text-[#F5EEE4] shadow-[0_24px_60px_rgba(23,19,14,0.28)] md:px-10 md:py-10'>
                    <div
                        className='pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E4D5A8]/70 to-transparent'
                        aria-hidden
                    />
                    <div className='flex flex-col gap-8 md:flex-row md:items-stretch md:gap-12'>
                        <div className='flex min-w-0 flex-1 flex-col gap-5'>
                            <div className='flex flex-wrap items-center gap-3'>
                                <div className='flex -space-x-2.5'>
                                    {ASESORIA_ANCHOR.faces.map(face => (
                                        <img
                                            key={face.name}
                                            src={face.src}
                                            alt={face.alt}
                                            className='h-11 w-11 rounded-full object-cover ring-2 ring-[#241C16]'
                                        />
                                    ))}
                                </div>
                                <p className='m-0 text-[12px] font-bold uppercase tracking-[0.14em] text-[#E4D5A8]'>
                                    {ASESORIA_ANCHOR.eyebrow}
                                </p>
                            </div>

                            <div className='flex flex-col gap-3'>
                                <h3 className='m-0 text-[24px] font-semibold leading-tight tracking-[-0.02em] md:text-[30px]'>
                                    {ASESORIA_ANCHOR.label}
                                </h3>
                                <p className='m-0 max-w-[52ch] text-[15px] leading-[1.55] text-[#C9C0B3] md:text-[16px]'>
                                    {ASESORIA_ANCHOR.forYouIf}
                                </p>
                            </div>

                            <div className='flex max-w-[360px] flex-col gap-1.5 rounded-[16px] bg-[#17130E]/55 px-4 py-3.5'>
                                <p className='m-0 text-[15px] font-bold leading-snug text-[#F5EEE4]'>
                                    Solo cogemos 1 cliente al mes
                                </p>
                                <p className='m-0 text-[13px] leading-snug text-[#A89F90]'>
                                    {ASESORIA_ANCHOR.capacityNote}
                                </p>
                            </div>

                            <ul className='m-0 flex list-none flex-col gap-2.5 p-0'>
                                {ASESORIA_ANCHOR.includes.map(item => (
                                    <li key={item} className='flex items-start gap-2.5'>
                                        <Check
                                            className='mt-0.5 h-[18px] w-[18px] shrink-0 text-[#E4D5A8]'
                                            strokeWidth={2.75}
                                            aria-hidden
                                        />
                                        <span className='text-[14px] font-medium leading-snug text-[#F5EEE4]'>
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className='flex w-full shrink-0 flex-col justify-between gap-5 border-[#E4D5A8]/20 md:w-[300px] md:border-l md:pl-10'>
                            <div className='flex flex-col gap-4'>
                                <div>
                                    <p className='m-0 text-[11px] font-bold uppercase tracking-[0.14em] text-[#A89F90]'>
                                        Plan anual
                                    </p>
                                    <p className='m-0 mt-2 text-[40px] font-extrabold leading-none tracking-[-0.035em]'>
                                        {ASESORIA_ANCHOR.price}
                                    </p>
                                    <p className='mt-2 text-[13px] leading-snug text-[#A89F90]'>
                                        {ASESORIA_ANCHOR.priceNote}
                                    </p>
                                </div>
                                <div className='rounded-[16px] border border-[#E4D5A8]/35 bg-[#17130E]/40 px-4 py-3.5'>
                                    <p className='m-0 text-[28px] font-extrabold leading-none tracking-[-0.03em] text-[#E4D5A8]'>
                                        {ASESORIA_ANCHOR.priceVariable}
                                    </p>
                                    <p className='mt-2 text-[13px] font-semibold leading-snug text-[#F5EEE4]'>
                                        {ASESORIA_ANCHOR.priceVariableLabel}
                                    </p>
                                    <p className='mt-1.5 text-[12px] leading-snug text-[#A89F90]'>
                                        Se liquida trimestralmente. Si no mejora, no pagas variable.
                                    </p>
                                </div>
                            </div>
                            <button
                                type='button'
                                onClick={() => onSelectPath(ASESORIA_ANCHOR.offerPathId)}
                                className='inline-flex min-h-12 items-center justify-center rounded-full bg-[#D13D1A] px-4 text-center text-[14px] font-bold text-white shadow-[0_4px_14px_rgba(209,61,26,0.35)] transition-colors hover:bg-[#C03214]'
                            >
                                {ASESORIA_ANCHOR.cta}
                            </button>
                        </div>
                    </div>
                </article>

                <p className='mt-8 text-center text-[15px] leading-[1.55] text-[#5C5548] md:text-[16px]'>
                    <span className='font-semibold text-[#241A12]'>{TOOL_ONLY_EXIT.lead}</span> {TOOL_ONLY_EXIT.body}{' '}
                    <LocaleLink
                        to={ROUTES.home}
                        onClick={trackToolOnlyExit}
                        className='font-bold text-[#F4502A] underline-offset-2 transition-opacity hover:underline hover:opacity-85'
                    >
                        {TOOL_ONLY_EXIT.cta}
                    </LocaleLink>
                </p>
            </div>
        </section>
    )
}
